import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

export default function SplashScreen({ onComplete }) {
  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const textRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        setTimeout(() => {
          setIsAnimating(false);
          onComplete?.();
        }, 300);
      }
    });

    // Initial setup
    gsap.set('.splash-line', { scaleX: 0, transformOrigin: 'left center' });
    gsap.set('.splash-particle', { scale: 0, opacity: 0 });
    gsap.set('.splash-logo', { scale: 0.5, opacity: 0, rotationY: -90 });
    gsap.set('.splash-tagline', { y: 20, opacity: 0 });
    gsap.set('.splash-glow', { scale: 0, opacity: 0 });
    gsap.set('.pulse-ring', { scale: 0.5, opacity: 0 });

    // Animation sequence
    tl
      // Background glow
      .to('.splash-glow', {
        scale: 1.5,
        opacity: 0.3,
        duration: 1,
        ease: 'power2.out'
      })
      // Animated lines sweep in
      .to('.splash-line', {
        scaleX: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power3.inOut'
      }, '-=0.5')
      // Lines sweep out
      .to('.splash-line', {
        scaleX: 0,
        transformOrigin: 'right center',
        duration: 0.4,
        stagger: 0.05,
        ease: 'power2.in'
      }, '+=0.1')
      // Logo appears with 3D flip
      .to('.splash-logo', {
        scale: 1,
        opacity: 1,
        rotationY: 0,
        duration: 0.8,
        ease: 'back.out(1.7)'
      }, '-=0.3')
      // Pulse rings
      .to('.pulse-ring', {
        scale: 2,
        opacity: 0.5,
        duration: 0.6,
        stagger: 0.15,
        ease: 'power2.out'
      }, '-=0.4')
      .to('.pulse-ring', {
        scale: 3,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power2.out'
      }, '-=0.3')
      // Particles burst
      .to('.splash-particle', {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        stagger: {
          each: 0.02,
          from: 'random'
        },
        ease: 'back.out(2)'
      }, '-=0.8')
      // Particles float away
      .to('.splash-particle', {
        y: 'random(-100, 100)',
        x: 'random(-100, 100)',
        opacity: 0,
        duration: 1,
        stagger: 0.02,
        ease: 'power2.out'
      }, '-=0.3')
      // Text reveals
      .to('.splash-text', {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: 'power3.out'
      }, '-=0.8')
      .to('.splash-tagline', {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: 'power3.out'
      }, '-=0.3')
      // Logo pulse
      .to('.splash-logo', {
        scale: 1.05,
        duration: 0.3,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: 1
      }, '-=0.2')
      // Final glow pulse
      .to('.splash-glow', {
        scale: 2,
        opacity: 0.5,
        duration: 0.5,
        ease: 'power2.out'
      }, '-=0.3')
      // Fade out everything
      .to('.splash-content', {
        scale: 1.1,
        opacity: 0,
        duration: 0.5,
        ease: 'power2.in'
      }, '+=0.5')
      .to('.splash-container', {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out'
      }, '-=0.2');

    return () => {
      tl.kill();
    };
  }, [onComplete]);

  if (!isAnimating) return null;

  return (
    <div 
      ref={containerRef}
      className="splash-container fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)' }}
    >
      {/* Animated background glow */}
      <div 
        className="splash-glow absolute w-[600px] h-[600px] rounded-full"
        style={{ 
          background: 'radial-gradient(circle, rgba(249,111,81,0.4) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }}
      />
      
      {/* Animated lines */}
      <div className="absolute inset-0 flex flex-col justify-center gap-8 px-20">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="splash-line h-1 rounded-full"
            style={{
              background: `linear-gradient(90deg, transparent, rgba(249,111,81,${0.3 + i * 0.1}), transparent)`,
              marginLeft: `${i * 5}%`,
              marginRight: `${(4 - i) * 5}%`
            }}
          />
        ))}
      </div>

      {/* Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="splash-particle absolute w-2 h-2 rounded-full"
            style={{
              background: `rgba(249,111,81,${0.3 + Math.random() * 0.7})`,
              left: `${30 + Math.random() * 40}%`,
              top: `${30 + Math.random() * 40}%`,
              boxShadow: '0 0 10px rgba(249,111,81,0.5)'
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="splash-content relative flex flex-col items-center">
        {/* Pulse rings behind logo */}
        <div className="absolute">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="pulse-ring absolute w-40 h-40 rounded-full border-2"
              style={{
                borderColor: `rgba(249,111,81,${0.3 - i * 0.1})`,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
          ))}
        </div>

        {/* Logo */}
        <div ref={logoRef} className="splash-logo relative" style={{ perspective: '1000px' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_syncpulse-preview/artifacts/m12tbw8s_2.png"
            alt="SyncPulse"
            className="h-32 w-auto drop-shadow-2xl"
            style={{ filter: 'drop-shadow(0 0 30px rgba(249,111,81,0.5))' }}
          />
        </div>

        {/* Text - Centered tagline only */}
        <div ref={textRef} className="mt-6 text-center">
          <p className="splash-tagline text-white/80 text-lg tracking-widest uppercase font-medium">
            AI-Powered Music Metadata
          </p>
        </div>
      </div>

      {/* Corner accents */}
      <div className="absolute top-10 left-10 w-20 h-20 border-l-2 border-t-2 border-white/10 rounded-tl-3xl" />
      <div className="absolute top-10 right-10 w-20 h-20 border-r-2 border-t-2 border-white/10 rounded-tr-3xl" />
      <div className="absolute bottom-10 left-10 w-20 h-20 border-l-2 border-b-2 border-white/10 rounded-bl-3xl" />
      <div className="absolute bottom-10 right-10 w-20 h-20 border-r-2 border-b-2 border-white/10 rounded-br-3xl" />
    </div>
  );
}
